import App from '@/models/App'
import OrderingOrdererShip from '@/models/OrderingOrdererShip'
import OrderingOrdererAccount from '@/models/OrderingOrdererAccount'
import OrderingOrdererEstimate from '@/models/OrderingOrdererEstimate'
import OrderingOrdererOrderStatusLog from '@/models/OrderingOrdererOrderStatusLog'
import OrderingOrdererOrderItem from '@/models/OrderingOrdererOrderItem'
import OrderingOrdererItemTopCategory from '@/models/OrderingOrdererItemTopCategory'

export default class OrderingOrdererOrder extends App {
    static get entity() {
        return 'OrderingOrdererOrders'
    }

    static all() {
        return this.query()
            .with('orderingShip')
            .with('orderingItemTopCategory')
            .with('orderingOrderItems.orderingItem.orderingItemCategory')
            .with('orderingOrderItems.orderingItem.orderingItemSubCategory')
            .with('orderingOrderItems.orderingItem.orderingItemImages')
            .with('orderingAccount.user')
            .with('chosenOrderingEstimate.orderingAccount.user')
            .with('lastRemandedLog.orderingAccount.user')
            .get()
    }

    static fields() {
        return {
            id: this.attr(''),
            orderingShipId: this.attr(''),
            orderingAccountId: this.attr(''),
            orderingItemTopCategoryId: this.attr(''),
            chosenOrderingEstimateId: this.attr(''),
            orderingDestinationCompanyId: this.attr(''),
            name: this.attr(''),
            status: this.attr(''),
            destinationPlace: this.attr(''),
            flow: this.attr(''),
            destinationAddress: this.attr(''),
            desiredDeliveryDate: this.attr(''),
            desiredDeliveryTimeType: this.attr(''),
            desiredDeliveryTimeDetail: this.attr(''),
            reasonForRemand: this.attr(''),
            commentCount: this.attr(0),
            documentCount: this.attr(0),
            createdAt: this.attr(''),
            updatedAt: this.attr(''),
            orderingShip: this.belongsTo(OrderingOrdererShip, 'orderingShipId'),
            orderingOrderItems: this.hasMany(
                OrderingOrdererOrderItem,
                'orderingOrderId'
            ),
            orderingEstimates: this.attr([]), // FIXME
            orderingDestinationCompany: this.attr({}), // FIXME
            orderingAccount: this.belongsTo(
                OrderingOrdererAccount,
                'orderingAccountId'
            ),
            chosenOrderingEstimate: this.belongsTo(
                OrderingOrdererEstimate,
                'chosenOrderingEstimateId'
            ),
            hasUnreadComment: this.attr(false),
            lastRemandedLog: this.hasOne(
                OrderingOrdererOrderStatusLog,
                'orderingOrderId'
            ),
            orderingItemTopCategory: this.belongsTo(
                OrderingOrdererItemTopCategory,
                'orderingItemTopCategoryId'
            ),
        }
    }

    static apiConfig = {
        actions: {
            fetch(orderingShipId, params) {
                return this.get(`/ordering/orderer/${orderingShipId}/orders`, {
                    params: App.get(params),
                    headers: {
                        Authorization: 'Bearer ' + App.apiToken(),
                        'Accept-Language': App.acceptLanguage(),
                    },
                    dataKey: 'data',
                    baseURL: App.apiURL(),
                })
            },
            create(orderingShipId, params) {
                return this.post(
                    `/ordering/orderer/${orderingShipId}/orders`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            update(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}`,
                    App.patch(params),
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            cancelEstimateRequest(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/cancelEstimateRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            remandEstimateRequest(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/remandEstimateRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            reRequestEstimateRequest(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/reRequestEstimateRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            confirmEstimateRequest(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/confirmEstimateRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            submitEstimateRequest(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/submitEstimateRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            skipEstimateRequest(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/skipEstimateRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            submitReceipt(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/submitReceipt`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            manualEstimateRequest(orderingShipId, orderingOrderId, params) {
                return this.post(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/manualEstimateRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            desiredEstimateRequest(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/desiredEstimateRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            skipShippingRequest(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/skipShippingRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            revertToRequestConfirming(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/revertToRequestConfirming`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            revertToEstimateReceived(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/revertToEstimateReceived`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            revertToProductShipped(orderingShipId, orderingOrderId, params) {
                return this.patch(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/revertToProductShipped`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            sendEstimatePdfRequest(orderingShipId, orderingOrderId, params) {
                return this.post(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/sendEstimatePdfRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            },
            senderPdfRequest(orderingShipId, orderingOrderId, params) {
                return this.post(
                    `/ordering/orderer/${orderingShipId}/orders/${orderingOrderId}/senderPdfRequest`,
                    params,
                    {
                        headers: {
                            Authorization: 'Bearer ' + App.apiToken(),
                            'Accept-Language': App.acceptLanguage(),
                        },
                        baseURL: App.apiURL(),
                    }
                )
            }
        },
    }
}
